import ButtonStyled from '@/components/Buttons/Button';
import IconButton from '@/components/Buttons/IconButton';
import Loader from '@/components/Loaders/Loader';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import { toast } from '@/components/toast';
import { studioDomain } from '@/environment';
import DeleteIcon from '@/images/Bin.svg';
import CheckCircle from '@/images/CheckCircle.svg';
import InfoCircle from '@/images/circles/InfoCirclePrimary.svg';
import logo from '@/images/did_logo grey.svg';
import Embedding from '@/images/Embedding.svg';
import { useRefetchAgents, useUpdateAgent } from '@/pages/agents/api';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { AgentShareActiveTab, useClientKey, usePatchClientKey } from '@/state/agent';
import { useAgentEditorStore } from '@/state/root';
import theme from '@/theme';
import { H2 } from '@blueprintjs/core';
import { IAgent, IAgentKey } from '@d-id/types';
import styled from '@emotion/styled';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Dialog, InputAdornment, TextField } from '@mui/material';
import Tooltip, { tooltipClasses, TooltipProps } from '@mui/material/Tooltip';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { AgentShareEmbedCode } from './AgentShareEmbedCode';
import { BrandingToolTip } from './BrandingToolTip';

const StyledDialog = styled(Dialog)`
    .MuiDialog-paper {
        border-radius: 1.25rem;
        display: flex;
        width: 917px;
        max-width: 917px;
    }
`;

const ModalContent = styled('div')`
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 24px;
`;

const TitleH2 = styled(H2)`
    color: ${theme.colors.secondary.main};
    font-size: 24px !important;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    letter-spacing: -0.48px;
    height: fit-content;
    margin: 0;
`;

const WarningDiv = styled.div`
    display: flex;
    padding: 10px 16px;
    align-items: center;
    border-radius: 8px;
    gap: 8px;
    background: ${theme.colors.primary.bgLight};
    border: 1px solid ${theme.colors.primary.main};
`;

const CopiedDiv = styled(WarningDiv)`
    background: ${theme.colors.success[100]};
    border: 1px solid ${theme.colors.success[550]};
    height: 56px;
    margin-top: 16px;
`;

const WarningTextCredits = styled.span`
    color: ${theme.colors.secondary.main};
    font-family: Manrope, sans-serif;
    font-size: 1.6rem;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    letter-spacing: -0.32px;
`;

const IconWarningCredits = styled.img`
    display: flex;
    width: 24px;
    height: 24px;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
`;

const IconTitle = styled.div`
    margin-left: -30px;
    display: flex;
    align-items: center;
`;

const SubParagraphTitle = styled.span`
    color: ${theme.colors.secondary.main};
    font-size: 1.6rem;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
`;

const SubParagraph = styled.span`
    color: ${theme.colors.black[900]};
    margin-top: 4px;
    font-size: 1.4rem;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    display: block;
`;

const ClosetButton = styled(ButtonStyled)`
    width: 76px;
    height: 44px;
    margin-left: auto;
`;

const IconContainer = styled.img`
    margin-right: auto;
    margin-left: auto;
    display: flex;
`;

const IconForTitle = styled.img`
    margin-right: 6px;
    display: flex;
`;

const ToolTipContainer = styled.span`
    color: ${theme.colors.black[900]};
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 4px;
    font-size: 1.4rem;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
`;

const ContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    flex-shrink: 0;
`;

const StyledBox = styled('div')<{ hasError: boolean }>`
    border: 1px solid ${props => (props.hasError ? theme.colors.error.main : theme.colors.black[400])};
    border-radius: 0.8rem;
    padding: 0px 24px;
    margin-top: 20px;
`;

const TextFieldsContainer = styled('div')`
    display: flex;
    flex-direction: column;
`;

const StyledBoxPartionWithPadding = styled(Box)`
    width: 100%;
    margin-left: 15px;
    padding-left: 15px;
    padding-right: 15px;
`;

const ErrorText = styled('p')`
    color: red;
    margin-top: 8px;
    font-size: 14px;
`;

const WarningText = styled('p')`
    border-radius: 8px;
    color: ${theme.colors.error.main};
    background-color: ${theme.colors.error[100]};
    margin-top: 10px;
    font-family: Mulish;
    font-size: 1.2rem;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    padding: 10px 16px;
    color: var(--Danger-Dark, #cc0037);
`;
const WarningTextBold = styled.span`
    font-weight: 700;
`;

const StyledTextField = styled(TextField)`
    border-radius: 8px;
    .MuiInput-underline:after {
        border-bottom: 1px solid ${theme.colors.black[400]} !important;
    }

    .MuiInput-underline:before {
        border-bottom: 1px solid ${theme.colors.black[400]} !important;
    }

    &:hover {
        .MuiInput-underline:hover:not(.Mui-disabled):before {
            border-bottom: 1px solid ${theme.colors.black[400]} !important;
        }
    }

    &:focus-within {
        .MuiInput-underline:hover:not(.Mui-disabled):before {
            border-bottom: 1px solid ${theme.colors.black[400]} !important;
        }
    }

    .MuiInput-input {
        padding-top: 10px;
        padding-bottom: 10px;
    }

    & > div {
        min-height: 66px;
    }
`;

const AddDomainButton = styled(Button)`
    min-width: 87px;
    height: 34px;
    padding: 7px 8px;
    font-size: 14px;
    border-radius: 8px;
    font-family: 'Manrope', sans-serif !important;
    text-transform: none;
    box-shadow: none;

    :hover {
        box-shadow: none;
        background: ${theme.colors.secondary[800]};
    }
`;

const ConfirmButton = styled(Button)`
    height: 34px;
    margin-top: 10px;
    margin-left: 5px;
    color: ${theme.colors.white};
    font-size: 14px;
    border-radius: 0.5rem;
    background: ${theme.colors.error.main};
    font-family: 'Manrope', sans-serif !important;
    text-transform: none;
    font-weight: 600;
    padding: 0px;

    &:hover {
        background: ${theme.colors.error.hover};
    }
`;

const CancelButton = styled(Button)`
    height: 34px;
    margin-top: 10px;
    margin-right: 5px;
    border: 1px solid ${theme.colors.black[400]};
    color: ${theme.colors.black[900]};
    font-size: 14px;
    border-radius: 0.5rem;
    background: ${theme.colors.white};
    font-family: 'Mulish', sans-serif !important;
    text-transform: capitalize;
    font-weight: 600;
    padding: 0px;

    &:hover {
        background: ${theme.colors.white};
    }
`;

const StyledIconButton = styled(IconButton)`
    margin-top: 10px;
`;

const StyledIconContainer = styled.div`
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    vertical-align: middle;
    text-decoration: none;
    text-align: center;
    flex: 0 0 auto;
    border-radius: 50%;
    margin-top: 10px;
`;
const StyledIconInputs = styled.img`
    text-align: center;
    font-size: 1.5rem;
    box-sizing: inherit;
    display: flex;
    margin-left: 16px;
`;

const StyledLoaderInput = styled(Loader)`
    width: 16px;
    height: 16px;
`;

const ButtonDiv = styled('div')`
    margin-bottom: 10px;
`;

const CloseButton = styled(IconButton)`
    position: absolute;
    top: 21px;
    right: 20px;
`;

const TextTooltip = styled.span`
    font-family: Manrope, sans-serif;
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    letter-spacing: -0.32px;
    color: ${theme.colors.primary.main};
    cursor: pointer;
`;

const DomainsContainer = styled.div`
    margin-top: 24px;
`;

const ToolTip = styled(({ className, ...props }: TooltipProps) => <Tooltip {...props} classes={{ popper: className }} />)({
    [`& .${tooltipClasses.tooltip}`]: {
        maxWidth: '52rem',
        fontSize: '1.4rem',
        background: theme.colors.secondary.main,
    },
});

enum DomainDeletionState {
    NotStarted = 'NotStarted',
    ToConfirm = 'ToConfirm',
    Confirmed = 'Confirm',
    Deleted = 'Deleted',
}

interface Props {
    agent?: IAgent;
    open: boolean;
    onClose: () => void;
    errorHandler?: (error: any) => Promise<void>;
}

export const AgentShareModalV1 = observer(({ agent, open, onClose, errorHandler }: Props) => {
    const agentEditorStore = useAgentEditorStore();

    const mixpanel = useAnalytics();
    const { isAnonymous } = useAuth();
    const { data: clientKey } = useClientKey({ isAnonymous });
    const [domains, setDomains] = useState<string[]>([]);
    const [newDomain, setNewDomain] = useState('');
    const [hasError, setHasError] = useState(false);
    const { mutateAsync: patchClientKey, isLoading: isUpdating } = usePatchClientKey();
    const [domainDeleteStatus, setDomainDeleteStatus] = useState<DomainDeletionState[]>([]);
    const { mutateAsync: updateAgentMutation, isLoading } = useUpdateAgent(errorHandler);
    const [isAddingNewDomain, setIsAddingNewDomain] = useState(false);
    const { refetchAgents } = useRefetchAgents();

    const handleUpdateAgent = async () => {
        if (!agent?.id || (agent.access !== 'private' && agent.access !== 'unlisted')) return;
        const updateAgentAccess = {
            access: 'unlisted',
            embed: true,
        };
        await updateAgentMutation({ agentId: agent.id, update: updateAgentAccess });
        await refetchAgents();
    };

    useEffect(() => {
        if (open) {
            mixpanel?.track(`agent-share-agent`, { event: 'view', agentId: agent?.id });
        }
    }, [open, agent?.id]);

    useEffect(() => {
        if (open && clientKey) {
            setDomains(subtractStudioDomains(clientKey.allowed_domains ?? []));
        }
    }, [clientKey, open]);

    useEffect(() => {
        if (open) {
            if (!agent?.embed) handleUpdateAgent();
        }
    }, [open]);

    const handleAddDomain = async () => {
        if (isValidURL(newDomain)) {
            setIsAddingNewDomain(true);
            if (domains.indexOf(newDomain) !== -1) {
                toast.error({ title: `Cannot add duplicated domain` });
                return;
            }
            const updatedDomains = [...domains, newDomain];
            const update = {
                client_key: clientKey?.client_key,
                allowed_domains: [studioDomain, ...updatedDomains],
            } as IAgentKey;
            await patchClientKey(update);

            setDomains(updatedDomains);
            setNewDomain('');
            setHasError(false);
            setIsAddingNewDomain(false);
            agentEditorStore.setIsCodeSnippetCopied(false);
        } else {
            setHasError(true);
            setIsAddingNewDomain(false);
        }
    };

    const handleDeleteDomain = async (index: number) => {
        const updatedDomains = [...domains];
        updatedDomains.splice(index, 1);
        setDomains(updatedDomains);
        const update = {
            client_key: clientKey?.client_key,
            allowed_domains: [studioDomain, ...updatedDomains],
        } as IAgentKey;
        await patchClientKey(update);
        const updatedDomainDeleteStatus = [...domainDeleteStatus];
        updatedDomainDeleteStatus.splice(index, 1);
        setDomainDeleteStatus(updatedDomainDeleteStatus);
        if (!updatedDomains.length) {
            agentEditorStore.setIsCodeSnippetCopied(false);
        }
    };

    const handleDomainStateChange = (index: number, status: DomainDeletionState) => {
        const updatedDomainDeleteStatus = [...domainDeleteStatus];
        updatedDomainDeleteStatus[index] = status;
        setDomainDeleteStatus(updatedDomainDeleteStatus);
    };

    const isValidURL = (url: string) => {
        try {
            new URL(url);
            return true;
        } catch (error) {
            return false;
        }
    };

    const subtractStudioDomains = (domains: string[]) => {
        return domains.filter(item => item !== studioDomain);
    };

    const handleOnClose = () => {
        setDomains([]);
        setHasError(false);
        onClose();
        setDomainDeleteStatus([]);
        agentEditorStore.setShareActiveTab(AgentShareActiveTab.Embed);
        agentEditorStore.setIsCodeSnippetCopied(false);
    };

    const title = `Each agent minute will consume 2 credits`;
    const showBrandingToolTip = false; //user?.planGroup !== PlanGroup.ENTERPRISE;

    if (isLoading) {
        return null;
    }

    return (
        <StyledDialog open={open} onClose={handleOnClose}>
            <ModalContent>
                <TitleH2>Embed your agent</TitleH2>
                <CloseButton ariaLabel="close" onClick={handleOnClose}>
                    <CloseIcon color="secondary" fontSize="large" />
                </CloseButton>
                <WarningDiv>
                    <IconWarningCredits src={InfoCircle} />
                    <WarningTextCredits>When users converse with your Agent, it will consume sessions</WarningTextCredits>
                    <ToolTip placement="top" title={title}>
                        <TextTooltip>Learn more</TextTooltip>
                    </ToolTip>
                </WarningDiv>
                {showBrandingToolTip && (
                    <ContentContainer>
                        <StyledBoxPartionWithPadding hidden={agentEditorStore.shareActiveTab !== AgentShareActiveTab.Embed}>
                            <IconTitle>
                                {<IconForTitle src={logo} />}
                                <SubParagraphTitle>Hide D-ID branding</SubParagraphTitle>
                            </IconTitle>
                            <ToolTipContainer>
                                Whether or not you want to hide "Powered by D-ID" at the bottom
                                <BrandingToolTip />
                            </ToolTipContainer>
                        </StyledBoxPartionWithPadding>
                    </ContentContainer>
                )}
                <ContentContainer>
                    <StyledBoxPartionWithPadding hidden={agentEditorStore.shareActiveTab !== AgentShareActiveTab.Embed}>
                        <IconTitle>
                            {domains.length > 0 ? <IconForTitle src={CheckCircle} /> : <IconForTitle src={Embedding} />}
                            <SubParagraphTitle>Select which domains you want your agent to display on</SubParagraphTitle>
                        </IconTitle>
                        {Array.from(domainDeleteStatus.values()).includes(DomainDeletionState.ToConfirm) &&
                            domains.length == 1 &&
                            !isUpdating && (
                                <WarningText>
                                    <WarningTextBold>Warning: </WarningTextBold>
                                    You need at least one domain to embed this agent. Doing this will disable access to your agent
                                </WarningText>
                            )}
                        <StyledBox hasError={hasError}>
                            <TextFieldsContainer>
                                <StyledTextField
                                    variant="standard"
                                    type="text"
                                    fullWidth
                                    value={studioDomain}
                                    disabled
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <ButtonDiv>
                                                    <StyledIconButton ariaLabel={''} disabled>
                                                        <MaskedIcon src={DeleteIcon} size="16px" color={theme.colors.secondary[400]} />
                                                    </StyledIconButton>
                                                    <StyledIconContainer>
                                                        <StyledIconInputs src={CheckCircle} width="16px" height="16px" />
                                                    </StyledIconContainer>
                                                </ButtonDiv>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                {domains.map((domain, index) => (
                                    <StyledTextField
                                        variant="standard"
                                        fullWidth
                                        type="text"
                                        value={domain}
                                        key={domain}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <ButtonDiv>
                                                        {domainDeleteStatus[index] !== DomainDeletionState.ToConfirm && (
                                                            <StyledIconButton
                                                                onClick={() =>
                                                                    handleDomainStateChange(index, DomainDeletionState.ToConfirm)
                                                                }
                                                                ariaLabel={''}>
                                                                <IconContainer src={DeleteIcon} />
                                                            </StyledIconButton>
                                                        )}
                                                        {!isUpdating && domainDeleteStatus[index] !== DomainDeletionState.ToConfirm && (
                                                            <StyledIconContainer>
                                                                <StyledIconInputs src={CheckCircle} width="16px" height="16px" />
                                                            </StyledIconContainer>
                                                        )}
                                                        {isUpdating && index === domains.length - 1 && isAddingNewDomain && (
                                                            <StyledIconButton ariaLabel="">
                                                                <StyledLoaderInput color="black" />
                                                            </StyledIconButton>
                                                        )}
                                                        {domainDeleteStatus[index] === DomainDeletionState.ToConfirm && (
                                                            <>
                                                                <CancelButton
                                                                    onClick={() =>
                                                                        handleDomainStateChange(index, DomainDeletionState.NotStarted)
                                                                    }>
                                                                    Cancel
                                                                </CancelButton>
                                                                <ConfirmButton onClick={() => handleDeleteDomain(index)}>
                                                                    Confirm
                                                                </ConfirmButton>
                                                            </>
                                                        )}
                                                    </ButtonDiv>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                ))}
                                <StyledTextField
                                    variant="standard"
                                    placeholder={!domains.length ? 'You haven’t added any domains yet' : 'https://yourdomain.com'}
                                    fullWidth
                                    value={newDomain}
                                    onChange={e => {
                                        setNewDomain(e.target.value);
                                        setHasError(false);
                                    }}
                                    disabled={isUpdating}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter') {
                                            handleAddDomain();
                                        }
                                    }}
                                    InputProps={{
                                        disableUnderline: true,
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <AddDomainButton variant="contained" color="secondary" onClick={handleAddDomain}>
                                                    Add URL
                                                </AddDomainButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </TextFieldsContainer>
                        </StyledBox>
                        {hasError && <ErrorText>Validation failed. Please try again</ErrorText>}
                        {domains.length > 0 && (
                            <DomainsContainer>
                                <IconTitle>
                                    {!agentEditorStore.isCodeSnippetCopied ? (
                                        <IconForTitle src={Embedding} />
                                    ) : (
                                        <IconForTitle src={CheckCircle} />
                                    )}
                                    <SubParagraphTitle>Copy the code snippet</SubParagraphTitle>
                                </IconTitle>
                                <SubParagraph>
                                    Copy the snippet below and paste in your HTML code where you want to display your agent
                                </SubParagraph>
                                <AgentShareEmbedCode
                                    agent={agent}
                                    clientKey={clientKey}
                                    domains={domains}
                                    isCopied={agentEditorStore.isCodeSnippetCopied}
                                />
                                {agentEditorStore.isCodeSnippetCopied && (
                                    <CopiedDiv>
                                        <IconWarningCredits src={CheckCircle} />
                                        <WarningTextCredits>
                                            Great! Now paste the code snippet in the HTML code where you want your agent to display
                                        </WarningTextCredits>
                                    </CopiedDiv>
                                )}
                            </DomainsContainer>
                        )}
                    </StyledBoxPartionWithPadding>
                </ContentContainer>
                <ClosetButton color="secondary" variant="outlined" onClick={handleOnClose}>
                    Close
                </ClosetButton>
            </ModalContent>
        </StyledDialog>
    );
});
